import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-3">{children}</p>
);

const Support = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 md:p-12">

          <h1 className="text-3xl font-bold text-gray-900 mb-1">Support</h1>
          <p className="text-sm text-gray-500 mb-10">FieldHouse Sports — Help &amp; Contact</p>

          <Section title="Contact Us">
            <P>
              Our team is here to help. Reach us by email and we'll respond within one business day.
            </P>
            <div className="bg-gray-50 rounded-lg p-5 text-gray-700 text-sm space-y-1 mb-3">
              <p><strong>FieldHouse Sports</strong></p>
              <p>
                Email:{' '}
                <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">
                  support@fieldhouse.club
                </a>
              </p>
              <p>
                Website:{' '}
                <a href="https://www.fieldhouse.club" className="text-blue-600 underline">
                  www.fieldhouse.club
                </a>
              </p>
            </div>
            <P>
              When writing in, please include your account email address and a brief description of
              the issue so we can look into it quickly.
            </P>
          </Section>

          <Section title="Frequently Asked Questions">
            <div className="space-y-5">
              <div>
                <p className="font-medium text-gray-900 mb-1">How do I reset my password?</p>
                <P>
                  On the login screen, tap <strong>Forgot Password</strong> and enter your account
                  email address. You'll receive a reset link within a few minutes. Check your spam
                  folder if it doesn't arrive.
                </P>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-1">How do I join a team or organization?</p>
                <P>
                  Your coach or organization administrator will send you an invite link. Open the
                  link on your device and follow the prompts to create an account or sign in and
                  join automatically.
                </P>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-1">How do I manage push notifications?</p>
                <P>
                  Go to your device's Settings app, find FieldHouse under notifications, and adjust
                  your preferences there. You can also update in-app notification settings from your
                  profile page.
                </P>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-1">How do I opt out of SMS messages?</p>
                <P>
                  Reply <strong>STOP</strong> to any SMS message from FieldHouse. You can also turn
                  off SMS in your account notification settings inside the app.
                </P>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-1">How do I delete my account?</p>
                <P>
                  Open the app, go to <strong>Profile</strong>, scroll to the bottom, and tap{' '}
                  <strong>Delete My Account</strong>. You'll be asked to confirm with your password.
                  Account deletion is permanent and removes all associated data. If you have trouble
                  accessing your account, email us at{' '}
                  <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">
                    support@fieldhouse.club
                  </a>{' '}
                  and we'll complete the deletion for you.
                </P>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-1">How do payments and subscriptions work?</p>
                <P>
                  FieldHouse subscriptions are purchased on the web via Stripe. Payments for team
                  invoices can be made through Stripe (card) or PayPal depending on how your
                  organization is set up. For billing questions or subscription cancellations, email
                  us and include your account email.
                </P>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-1">What data is sent to AI services?</p>
                <P>
                  The AI video analysis feature sends frames extracted from your uploaded video to
                  OpenAI's API to generate coaching feedback. Frames are not retained by OpenAI for
                  model training. No other personal information is included. You'll be asked for
                  consent the first time you use this feature. See our{' '}
                  <a href="/privacy-policy" className="text-blue-600 underline">
                    Privacy Policy
                  </a>{' '}
                  for full details.
                </P>
              </div>
            </div>
          </Section>

          <Section title="Privacy Policy">
            <P>
              For information on how FieldHouse collects, uses, and protects your personal data,
              please review our{' '}
              <a href="/privacy-policy" className="text-blue-600 underline">
                Privacy Policy
              </a>
              .
            </P>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default Support;
